import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { ModuleRegistry } from "ag-grid-community";
import { ClipboardModule } from "ag-grid-enterprise";
import { ContextMenuModule } from "ag-grid-enterprise";
import { IntegratedChartsModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import isNil from "lodash/isNil";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useColumnTypes } from "../../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import { useAggFuncs } from "../../../_src_shared/utils/agGrid/hooks/useAggFuncs";
import { useSidebar } from "../../../_src_shared/utils/agGrid/hooks/useSidebar";
import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import { colDefFilterMultiSelect } from "../../../utils/agGrid/colDefs/colDefFilterMultiSelect";
import { useColumnDefsLoanPipelineTable } from "./hooks/useColumnDefsLoanPipelineTable";
import { useDefaultColumnDefsLoanPipelineTable } from "./hooks/useDefaultColumnDefsLoanPipelineTable";
import { LOAN_STATUS_LABEL_ALL_ACTIVE } from "../../../constants/loanPipeline/LOAN_STATUS_LABEL_ALL_ACTIVE";
import { useLoanPipelineScreenHook } from "../useLoanPipelineScreenHook";
import { LOAN_STATUS_LABEL_ALL_INACTIVE_LOANS } from "../../../constants/loanPipeline/LOAN_STATUS_LABEL_ALL_INACTIVE_LOANS";
import { LOAN_STATUS_LABEL_ALL_CLOSED_LOANS } from "../../../constants/loanPipeline/LOAN_STATUS_LABEL_ALL_CLOSED_LOANS";
import { LOAN_PIPELINE_FILTER_LABEL_ALL } from "../../../constants/loanPipeline/LOAN_PIPELINE_FILTER_LABEL_ALL";
import { amber, blue, green, red, yellow } from "@mui/material/colors";

ModuleRegistry.registerModules([
  IntegratedChartsModule.with(AgChartsEnterpriseModule),
  ContextMenuModule,
  ClipboardModule,
]);

const LoanPipelineTable = forwardRef(
  (
    {
      rowData,
      loading,
      quickFilterText,
      onStateUpdated,
      onLoanSelect,
      height = "100%",
      pipelineFilter = null,
      loanStatusFilter = null,
      advancedFiltering = false,
      onFirstDataRendered,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const {
      loanStatusListActiveLoans,
      loanStatusListClosedLoans,
      loanStatusListInactiveLoans,
    } = useLoanPipelineScreenHook();
    const columnDefs = useColumnDefsLoanPipelineTable();
    const defaultColDef = useDefaultColumnDefsLoanPipelineTable({
      floatingFilter: advancedFiltering,
      filter: colDefFilterMultiSelect,
      cellClass: "ag-cell-vertical-center",
      minWidth: 200,
    });
    const columnTypes = useColumnTypes();

    const aggFuncs = useAggFuncs();
    const sideBar = useSidebar();

    const gridApiRef = useRef(null);

    const onGridReady = useCallback((params) => {
      gridApiRef.current = params.api;
    }, []);

    const isExternalFilterPresent = useCallback(() => {
      return !isNil(pipelineFilter) || !isNil(loanStatusFilter);
    }, [pipelineFilter, loanStatusFilter]);

    const doesExternalFilterPass = useCallback(
      (node) => {
        const data = node.data;
        if (!data) return true;

        // Handle pipeline filtering - skip if LOAN_PIPELINE_FILTER_LABEL_ALL
        if (
          !isNil(pipelineFilter) &&
          pipelineFilter !== "" &&
          pipelineFilter !== LOAN_PIPELINE_FILTER_LABEL_ALL
        ) {
          if (data.pipeline !== pipelineFilter) {
            return false;
          }
        }
        const status = data.loanStatus;

        if (loanStatusFilter === "All") {
          return true;
        }

        if (!isNil(loanStatusFilter) && loanStatusFilter !== "") {
          if (loanStatusFilter === LOAN_STATUS_LABEL_ALL_ACTIVE) {
            if (loanStatusListActiveLoans.includes(status)) {
              return true;
            }
          }

          if (loanStatusFilter === LOAN_STATUS_LABEL_ALL_INACTIVE_LOANS) {
            // Check if the status is in the inactive loans list
            if (loanStatusListInactiveLoans.includes(status)) {
              return true;
            }
          }

          // ---
          if (loanStatusFilter === LOAN_STATUS_LABEL_ALL_CLOSED_LOANS) {
            if (loanStatusListClosedLoans.includes(status)) {
              return true;
            }
          }
        }

        if (status !== loanStatusFilter) {
          return false;
        }

        return true;
      },
      [
        pipelineFilter,
        loanStatusFilter,
        loanStatusListActiveLoans,
        loanStatusListClosedLoans,
        loanStatusListInactiveLoans,
      ]
    );

    const onCellClicked = useCallback(
      (params) => {
        if (params?.colDef?.field === "loanNumber") {
          return;
        }

        const clickedElement = params.event?.target;

        if (
          // (clickedElement instanceof Element &&
          //   clickedElement.closest("svg")) ||
          (clickedElement instanceof Element &&
            clickedElement.closest("button")) ||
          (clickedElement instanceof Element && clickedElement.closest("a"))
        ) {
          return;
        }

        // if (
        //   params?.colDef?.field === "loanStatus" ||
        //   params?.colDef?.field === "expectedClosingDate" ||
        //   params?.colDef?.field === "baseLoanAmount"
        // ) {
        //   onLoanSelect(params.data, params.colDef.field, params);
        //   return;
        // }

        // 4) In any other case, ensure we have a loanNumber before proceeding.
        if (isNil(params?.data?.loanNumber)) {
          console.warn("No loanNumber found on this row");
          return;
        }

        if (params?.colDef?.field === "loanName") {
          navigate(`/loan/${params.data.loanNumber}`);
        }
        // 5) Finally, navigate to the loan detail page for this row's loanNumber:
      },
      [onLoanSelect, navigate]
    );

    useEffect(() => {
      const api = gridApiRef.current;
      if (!api) return;

      api.onFilterChanged();
    }, [pipelineFilter, loanStatusFilter]);

    return (
      <AgGridTableWrapper height={height} size={2} noBorderRadius>
        <AgGridReact
          ref={ref}
          onGridReady={onGridReady}
          rowData={rowData}
          columnDefs={columnDefs}
          quickFilterText={quickFilterText}
          defaultColDef={defaultColDef}
          groupDisplayType={"groupRows"}
          onCellClicked={onCellClicked}
          columnTypes={columnTypes}
          sideBar={sideBar}
          getQuickFilterTex
          onStateUpdated={onStateUpdated}
          enableCharts={true}
          cellSelection={true}
          includeHiddenColumnsInQuickFilter
          aggFuncs={aggFuncs}
          onFirstDataRendered={onFirstDataRendered}
          suppressAggFuncInHeader={true}
          loading={loading}
          tooltipShowDelay={500}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
        />
      </AgGridTableWrapper>
    );
  }
);

export default LoanPipelineTable;
